// @mui
import { Link, ListItemText, Tooltip } from '@mui/material';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { NavItemProps } from '../types';

// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
// locales
import { useLocales } from '../../../locales';
//
import Iconify from '../../iconify';
import { StyledIcon, StyledItem } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef<HTMLDivElement, NavItemProps>(
  ({ active, depth, isExternalLink, item, open, ...other }, ref) => {
    const { translate } = useLocales();

    const { caption, children, disabled, icon, path, roles, title } = item;

    const subItem = depth !== 1;

    const renderContent = (
      <StyledItem
        active={active}
        depth={depth}
        disabled={disabled}
        open={open}
        ref={ref}
        {...other}
      >
        {icon && <StyledIcon>{icon}</StyledIcon>}

        <ListItemText
          primary={`${translate(title)}`}
          primaryTypographyProps={{
            noWrap: true,
            sx: {
              fontSize: 10,
              lineHeight: '16px',
              textAlign: 'center',
              width: 72,
              ...(active && {
                fontWeight: 'fontWeightMedium',
              }),
              ...(subItem && {
                fontSize: 14,
                textAlign: 'left',
                width: 'auto',
              }),
            },
          }}
        />

        {caption && (
          <Tooltip arrow placement="right" title={`${translate(caption)}`}>
            <Iconify
              icon="eva:info-outline"
              sx={{
                left: 6,
                position: 'absolute',
                top: 11,
              }}
              width={16}
            />
          </Tooltip>
        )}

        {!!children && (
          <Iconify
            icon="eva:chevron-right-fill"
            sx={{
              position: 'absolute',
              right: 6,
              top: 11,
            }}
            width={16}
          />
        )}
      </StyledItem>
    );

    const renderItem = () => {
      // ExternalLink
      if (isExternalLink)
        return (
          <Link href={path} rel="noopener" target="_blank" underline="none">
            {renderContent}
          </Link>
        );

      // Default
      return (
        <Link component={RouterLink} to={path} underline="none">
          {renderContent}
        </Link>
      );
    };

    return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
  }
);

NavItem.displayName = 'NavItem';

export default NavItem;
